/**
 * Analytics helpers for the board's UI layer.
 *
 * Extracted so `useBoardActions.ts` and `RequestsTab.tsx` stay under the 500-line lint ceiling, and
 * so the two judgement calls below live in one place with their reasoning attached rather than
 * inline where the next reader skims past them.
 */
import { logAnalyticsEvent } from '../../../../analytics/nativeAnalytics';

/**
 * Report a candidate moving between stages — including a rejection, which is a stage change to a
 * terminal stage.
 *
 * ONE event rather than two so "every stage change" is a single stream you can read end to end,
 * with rejections filterable, instead of two taxonomies needing a join.
 *
 * Stage names and Lever archive reasons are role CONFIGURATION — fixed lists, not anything a
 * candidate wrote — so they travel. A recruiter's free-text note never does. Call this only once
 * the change has actually SUCCEEDED: emitted on intent, a failed move or a cancelled archive would
 * count as progress that never happened.
 */
export function reportStageChange(input: {
  fromStage: string | null | undefined;
  toStage: string | null | undefined;
  isRejection: boolean;
  /** Fixed Lever archive reason, for a rejection only. */
  reason?: string;
  /** Whether the candidate was actually told. A silent move is its own finding. */
  withEmail: boolean;
}): void {
  void logAnalyticsEvent('ta_stage_changed', {
    from_stage: input.fromStage || 'unknown',
    to_stage: input.toStage || 'unknown',
    is_rejection: input.isRejection,
    ...(input.reason ? { reason: input.reason } : {}),
    with_email: input.withEmail,
  });
}

/**
 * Was the generated draft good enough to send untouched?
 *
 * Without this, "we drafted 200 replies" says nothing about whether the model helped — a draft
 * rewritten from scratch counts the same as one sent as-is. Comparing NORMALISED text keeps a
 * reformat from reading as a rewrite.
 *
 * Blunt on purpose: fixing one typo counts as edited. That under-claims the model's quality, which
 * is the right direction to be wrong in.
 */
export function draftAcceptedVerbatim(original: string | null | undefined, submitted: string): boolean {
  const normalise = (text: string) =>
    text
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  const before = normalise(original || '');
  return Boolean(before) && normalise(submitted) === before;
}

/**
 * Chasing interviewers for feedback. How often the SLA tab is needed says more about the interview
 * process than about the tab. Not routed through the API's `tracked` wrapper because the nudge
 * endpoint predates it and reports partial success rather than throwing.
 */
export function reportFeedbackNudge(sentCount: number, partial: boolean): void {
  void logAnalyticsEvent('ta_board_feedback_nudged', {
    interviewer_count: sentCount,
    partial,
  });
}

/** A hand-edited panel means the suggested one was wrong — this measures how good the suggestion is. */
export function reportPanelSaved(interviewerCount: number, slotsFound: number): void {
  void logAnalyticsEvent('ta_board_panel_saved', {
    interviewer_count: interviewerCount,
    slots_found: slotsFound,
  });
}
